/**
 * THE 2025 PRO SEASON ROSTER — D-EZ-76.
 *
 * Howard, 21 Aug 2026, put `2025 Roster.pdf` in `docs/` and asked for *"a seed
 * starting scenario for users to be able to use the 2025 season (drivers and
 * tracks) to start their career association."*
 *
 * ⚠ THIS FILE IS A TRANSCRIPTION AND IT IS THE LEDGER FOR THESE DRIVERS.
 *
 * Fifty-six printed cards read at 300 dpi: 51 drivers, four conditional
 * alternates, and Shane van Gisbergen's two cards. Every number below was read
 * off the card. The pedal bars in particular were counted from magnified crops
 * of the bar strip rather than from the sheet, because at sheet scale four bars
 * and five bars are the same smudge and several of the first-pass readings were
 * wrong by one.
 *
 * WHAT THE SYMBOLS ON A 2025 CARD MEAN, in the set's own words:
 *
 *   • The row of solid bars under the name is the GAS PEDAL (1-5).
 *   • The ✻ bursts on the right are the BRAKE PEDAL (0-3).
 *   • The letter inside the trophy is the PIT CREW GRADE; the word beneath it
 *     is the trophy. A card with no trophy prints the letter on its own.
 *   • A ★ before or after a quality means it is STARRED.
 *   • The GREEN thumbs-up triangle is NEW THIS YEAR and is not the old warning
 *     triangle. From the set's notes: *"any driver who, in real-life, did not
 *     have a race in which they failed to finish because of mechanical issues,
 *     has been given a green 'thumbs up' triangle. For these drivers, if the pit
 *     crew fails the REPAIR roll for a PROBLEM result, rather than being knocked
 *     out of the race, they instead go to the top (last place) of the BOTTOM
 *     group stack, but lose their ability to challenge or defend for the rest of
 *     the race."*
 *   • The YELLOW ⚠ triangle — Derek Kraus and Josh Bilicki only — is the
 *     classic one: a red 6 in any challenge or duel is an immediate PROBLEM.
 *
 * Those are two different rules on two different symbols, and conflating them
 * would turn an exemption into a penalty for eleven drivers.
 */

import type { AlternateCard, CardCondition, RaceConditions } from '../domain/altCards.ts';
import { alternateOf, conditionHolds, deltaFrom } from '../domain/altCards.ts';
import type { DriverCard, PitCrewGrade, Quality, Trophy } from '../domain/types.ts';

export type { CardCondition, RaceConditions };
export { conditionHolds };

/**
 * WHEN A DRIVER RACES OFF A DIFFERENT CARD — D-EZ-76.
 *
 * The 2025 set stopped using "boutique" grey-out ratings and issued alternate
 * cards instead. Its notes name all five, verbatim:
 *
 *   • Kyle Larson — *"drove well all year, but finished great in the playoffs.
 *     His only weakness was on road courses. But in the playoffs, he finished
 *     second on the Charlotte Roval. So, he gets SMART in the Playoff races
 *     only, which will help him there and on the Intermediate tracks, too."*
 *   • William Byron — *"performed better in the 'playoffs', finishing 3rd
 *     overall, and he gets HEAT in Playoff races only."*
 *   • Bubba Wallace — *"won at Indianapolis and when he finished a superspeedway
 *     race he averaged a fourth place finish, clearly the best. He gets STAR★,
 *     but the 'starred' STAR quality only applies on tracks 2.0 miles or greater
 *     (superspeedways). On tracks less than two miles, he has the STAR
 *     (unstarred) quality."*
 *   • Austin Dillon — *"gets HEAT★ for his win at Richmond and aggressiveness in
 *     the #3 (sound familiar?), but the 'starred' HEAT only applies on tracks
 *     less than one mile (short tracks). On tracks longer than one mile, he has
 *     the HEAT (unstarred) quality."*
 *   • Shane van Gisbergen — *"performances on road courses were dramatically
 *     better than on others. Thus, he's been given two cards for this set. Use
 *     the card with the 'R' on the hood for road courses, and the card with the
 *     'O' on the hood for any other track."*
 */
export interface Roster2025Card {
  driverId: string;
  name: string;
  carNumber: string;
  manufacturer: 'Ford' | 'Chevy' | 'Toyota';
  gasPedal: number;
  brakePedal: number;
  qualities: Quality[];
  starred: Quality[];
  escape: number;
  fastPit: number;
  trophy: Trophy;
  pitCrewGrade: PitCrewGrade;
  /** The classic warning triangle — a red 6 is an immediate PROBLEM. */
  triangle: boolean;
  /** The 2025 green thumbs-up: a failed REPAIR does not end his race. */
  dnfExempt: boolean;
  /**
   * An alternate card and the races it applies to. The base card above is used
   * everywhere else.
   */
  alternate?: {
    label: string;
    when: CardCondition;
    qualities: Quality[];
    starred: Quality[];
    gasPedal?: number;
    brakePedal?: number;
    escape?: number;
    fastPit?: number;
    trophy?: Trophy;
    pitCrewGrade?: PitCrewGrade;
    dnfExempt?: boolean;
  };
  /**
   * A car number several drivers share. The set marks the stand-ins with an "x"
   * on the hood and says: *"Please be sure to choose ONE driver for each team
   * before beginning the Race Week procedure, so as to avoid having driver
   * team-mates participating in the same race."*
   */
  standIn?: boolean;
}

const D = (
  driverId: string,
  name: string,
  carNumber: string,
  manufacturer: Roster2025Card['manufacturer'],
  gasPedal: number,
  brakePedal: number,
  qualities: Quality[],
  starred: Quality[],
  escape: number,
  fastPit: number,
  trophy: Trophy,
  pitCrewGrade: PitCrewGrade,
  flags: { triangle?: boolean; dnfExempt?: boolean; standIn?: boolean } = {},
  alternate?: Roster2025Card['alternate'],
): Roster2025Card => ({
  driverId,
  name,
  carNumber,
  manufacturer,
  gasPedal,
  brakePedal,
  qualities,
  starred,
  escape,
  fastPit,
  trophy,
  pitCrewGrade,
  triangle: flags.triangle ?? false,
  dnfExempt: flags.dnfExempt ?? false,
  ...(flags.standIn ? { standIn: true } : {}),
  ...(alternate ? { alternate } : {}),
});

export const ROSTER_2025: readonly Roster2025Card[] = [
  D('rw25-lajoie', 'Corey LaJoie', '01', 'Ford', 2, 0, ['SKILL', 'STAR'], [], 4, 2, null, 'E', { dnfExempt: true }),
  D('rw25-chastain', 'Ross Chastain', '1', 'Chevy', 4, 1, ['REFLEX', 'CONTROL', 'SKILL', 'SMART'], [], 7, 7, 'silver', 'B', { dnfExempt: true }),
  D('rw25-cindric', 'Austin Cindric', '2', 'Ford', 4, 0, ['REFLEX', 'SKILL', 'STAR', 'HEAT'], [], 8, 6, 'gold', 'B'),
  D('rw25-adillon', 'Austin Dillon', '3', 'Chevy', 4, 0, ['REFLEX', 'CONTROL', 'HEAT'], [], 8, 5, 'bronze', 'B', { dnfExempt: true },
    {
      label: 'Short Tracks',
      when: { kind: 'track-under', miles: 1 },
      qualities: ['REFLEX', 'CONTROL', 'HEAT'],
      starred: ['HEAT'],
    }),
  D('rw25-gragson', 'Noah Gragson', '4', 'Ford', 3, 1, ['SKILL', 'STAR', 'HEAT'], [], 5, 5, 'bronze', 'C', { dnfExempt: true }),
  D('rw25-larson', 'Kyle Larson', '5', 'Chevy', 5, 2, ['REFLEX', 'CONTROL', 'SKILL', 'STAR'], ['SKILL'], 9, 9, 'gold', 'A', {},
    {
      label: 'Playoffs',
      when: { kind: 'playoffs' },
      qualities: ['REFLEX', 'CONTROL', 'SKILL', 'STAR', 'SMART'],
      starred: ['SKILL'],
    }),
  D('rw25-keselowski', 'Brad Keselowski', '6', 'Ford', 5, 0, ['REFLEX', 'CONTROL', 'SKILL', 'STAR'], [], 7, 7, 'bronze', 'C'),
  D('rw25-haley', 'Justin Haley', '7', 'Chevy', 4, 1, ['REFLEX', 'STAR', 'SMART'], [], 8, 5, 'bronze', 'B', { dnfExempt: true }),
  D('rw25-kbusch', 'Kyle Busch', '8', 'Chevy', 4, 1, ['CONTROL', 'SKILL', 'SMART', 'HEAT'], [], 8, 6, 'silver', 'A', { dnfExempt: true }),
  D('rw25-elliott', 'Chase Elliott', '9', 'Chevy', 5, 1, ['REFLEX', 'CONTROL', 'SKILL', 'SMART', 'HEAT'], [], 8, 8, 'silver', 'B', { dnfExempt: true }),
  D('rw25-tdillon', 'Ty Dillon', '10', 'Chevy', 3, 0, ['CONTROL', 'STAR', 'HEAT'], [], 8, 4, 'bronze', 'B', { dnfExempt: true }),
  D('rw25-hamlin', 'Denny Hamlin', '11', 'Toyota', 5, 2, ['REFLEX', 'CONTROL', 'SKILL', 'STAR', 'HEAT'], ['CONTROL'], 9, 9, 'gold', 'B'),
  D('rw25-blaney', 'Ryan Blaney', '12', 'Ford', 5, 2, ['REFLEX', 'CONTROL', 'SKILL', 'STAR'], ['REFLEX'], 7, 9, 'gold', 'C'),
  D('rw25-allmendinger', 'A.J. Allmendinger', '16', 'Chevy', 4, 1, ['SKILL', 'SMART', 'HEAT'], [], 7, 6, 'silver', 'C'),
  D('rw25-buescher', 'Chris Buescher', '17', 'Ford', 4, 1, ['CONTROL', 'SKILL', 'STAR', 'SMART', 'HEAT'], [], 8, 7, 'gold', 'A', { dnfExempt: true }),
  D('rw25-briscoe', 'Chase Briscoe', '19', 'Toyota', 5, 2, ['CONTROL', 'SKILL', 'STAR', 'SMART'], ['STAR'], 9, 9, 'gold', 'B'),
  D('rw25-bell', 'Christopher Bell', '20', 'Toyota', 4, 2, ['REFLEX', 'CONTROL', 'SKILL', 'SMART', 'HEAT'], ['SKILL'], 9, 9, 'gold', 'B'),
  D('rw25-berry', 'Josh Berry', '21', 'Ford', 4, 1, ['REFLEX', 'CONTROL', 'STAR', 'HEAT'], [], 6, 6, 'silver', 'C'),
  D('rw25-logano', 'Joey Logano', '22', 'Ford', 5, 1, ['REFLEX', 'CONTROL', 'SKILL', 'HEAT'], [], 8, 7, 'gold', 'B', { dnfExempt: true }),
  D('rw25-wallace', 'Bubba Wallace', '23', 'Toyota', 5, 1, ['REFLEX', 'SKILL', 'STAR', 'SMART'], [], 6, 7, 'silver', 'C', {},
    {
      label: 'SuperSpeedways',
      when: { kind: 'track-at-least', miles: 2 },
      qualities: ['REFLEX', 'SKILL', 'STAR', 'SMART'],
      starred: ['STAR'],
    }),
  D('rw25-byron', 'William Byron', '24', 'Chevy', 5, 1, ['REFLEX', 'CONTROL', 'SKILL', 'SMART'], ['REFLEX'], 8, 8, 'gold', 'B', {},
    {
      label: 'Playoffs',
      when: { kind: 'playoffs' },
      qualities: ['REFLEX', 'CONTROL', 'SKILL', 'SMART', 'HEAT'],
      starred: ['REFLEX'],
    }),
  D('rw25-love', 'Jesse Love', '33', 'Chevy', 3, 0, ['REFLEX', 'SKILL'], [], 6, 3, null, 'B', { dnfExempt: true, standIn: true }),
  D('rw25-hill', 'Austin Hill', '33', 'Chevy', 3, 0, ['REFLEX', 'SMART', 'HEAT'], [], 11, 3, null, 'B', { dnfExempt: true }),
  D('rw25-gilliland', 'Todd Gilliland', '34', 'Ford', 3, 1, ['CONTROL', 'STAR', 'SMART'], [], 8, 5, 'bronze', 'A', { dnfExempt: true }),
  D('rw25-herbst', 'Riley Herbst', '35', 'Toyota', 3, 0, ['STAR', 'SMART'], [], 9, 2, 'bronze', 'C'),
  D('rw25-zsmith', 'Zane Smith', '38', 'Ford', 3, 1, ['REFLEX', 'CONTROL', 'SKILL'], [], 7, 5, 'bronze', 'C'),
  D('rw25-custer', 'Cole Custer', '41', 'Ford', 4, 0, ['SKILL', 'STAR', 'SMART'], [], 7, 5, 'bronze', 'B', { dnfExempt: true }),
  D('rw25-nemechek', 'John H. Nemechek', '42', 'Toyota', 4, 1, ['CONTROL', 'STAR', 'SMART'], [], 8, 6, 'bronze', 'B', { dnfExempt: true }),
  D('rw25-ejones', 'Erik Jones', '43', 'Toyota', 4, 1, ['CONTROL', 'SKILL', 'HEAT'], [], 7, 6, 'bronze', 'B', { dnfExempt: true }),
  D('rw25-gase', 'Joey Gase', '44', 'Chevy', 2, 0, ['STAR', 'HEAT'], [], 11, 2, null, 'D', { dnfExempt: true, standIn: true }),
  D('rw25-kraus', 'Derek Kraus', '44', 'Chevy', 2, 0, ['CONTROL', 'HEAT'], [], 11, 2, null, 'D', { triangle: true, standIn: true }),
  D('rw25-yeley', 'J.J. Yeley', '44', 'Chevy', 2, 0, ['SKILL', 'SMART'], [], 8, 2, null, 'D'),
  D('rw25-reddick', 'Tyler Reddick', '45', 'Toyota', 4, 1, ['REFLEX', 'CONTROL', 'STAR', 'SMART', 'HEAT'], [], 9, 7, 'gold', 'A', { dnfExempt: true }),
  D('rw25-stenhouse', 'Ricky Stenhouse, Jr.', '47', 'Chevy', 3, 1, ['CONTROL', 'SKILL', 'HEAT'], [], 7, 5, 'bronze', 'B', { dnfExempt: true }),
  D('rw25-bowman', 'Alex Bowman', '48', 'Chevy', 4, 1, ['REFLEX', 'CONTROL', 'SKILL', 'SMART', 'HEAT'], [], 8, 7, 'silver', 'B'),
  D('rw25-ware', 'Cody Ware', '51', 'Ford', 3, 0, ['CONTROL'], [], 6, 2, 'bronze', 'D'),
  D('rw25-gibbs', 'Ty Gibbs', '54', 'Toyota', 5, 0, ['REFLEX', 'SKILL', 'STAR', 'SMART'], [], 7, 6, 'gold', 'B', { dnfExempt: true }),
  D('rw25-preece', 'Ryan Preece', '60', 'Ford', 4, 1, ['REFLEX', 'CONTROL', 'STAR', 'SMART'], [], 8, 7, 'silver', 'A', { dnfExempt: true }),
  D('rw25-alfredo', 'Anthony Alfredo', '62', 'Chevy', 3, 0, ['SKILL', 'STAR', 'HEAT'], [], 10, 3, null, 'A', { dnfExempt: true }),
  D('rw25-finchum', 'Chad Finchum', '66', 'Ford', 2, 0, ['HEAT'], [], 5, 2, null, 'E', { standIn: true }),
  D('rw25-mears', 'Casey Mears', '66', 'Ford', 2, 0, ['REFLEX', 'STAR'], [], 11, 2, null, 'E', { standIn: true }),
  D('rw25-bilicki', 'Josh Bilicki', '66', 'Ford', 2, 0, ['REFLEX', 'SMART'], [], 11, 2, null, 'E', { triangle: true }),
  D('rw25-heim', 'Corey Heim', '67', 'Toyota', 3, 0, ['REFLEX', 'CONTROL', 'HEAT'], [], 5, 5, null, 'C', { dnfExempt: true }),
  D('rw25-mcdowell', 'Michael McDowell', '71', 'Chevy', 4, 1, ['REFLEX', 'CONTROL', 'SMART'], [], 8, 6, 'silver', 'A', { dnfExempt: true }),
  D('rw25-hocevar', 'Carson Hocevar', '77', 'Chevy', 4, 1, ['REFLEX', 'CONTROL', 'STAR'], [], 8, 6, 'silver', 'C'),
  D('rw25-mcleod', 'B.J. McLeod', '78', 'Chevy', 3, 0, ['CONTROL', 'STAR'], [], 6, 2, null, 'C', { standIn: true }),
  D('rw25-legge', 'Katherine Legge', '78', 'Chevy', 2, 0, ['REFLEX', 'SMART', 'HEAT'], [], 7, 2, null, 'C', { dnfExempt: true }),
  D('rw25-jjohnson', 'Jimmie Johnson', '84', 'Chevy', 5, 0, ['CONTROL', 'SKILL', 'STAR'], [], 5, 6, null, 'E', { dnfExempt: true }),
  D('rw25-zilisch', 'Connor Zilisch', '87', 'Chevy', 3, 0, ['CONTROL', 'SMART', 'HEAT'], [], 5, 3, null, 'D', { dnfExempt: true }),
  /*
    ⚠ THE ONLY DRIVER IN THE SET WITH TWO FULL CARDS rather than one card and a
    quality tweak. The road course card is not "the same man with a star on it":
    it is a different gas pedal, a different brake pedal, two extra qualities,
    two of them starred, four points of escape, six points of fast pit, a
    different crew grade and a different trophy. The base card here is the "O"
    hood, which the set says to use for every track that is not a road course.
  */
  D('rw25-svg', 'Shane van Gisbergen', '88', 'Chevy', 3, 0, ['SKILL', 'SMART', 'HEAT'], [], 7, 4, 'bronze', 'C', {},
    {
      label: 'Road Courses',
      when: { kind: 'road-course' },
      qualities: ['REFLEX', 'CONTROL', 'SKILL', 'STAR', 'SMART', 'HEAT'],
      starred: ['SMART', 'HEAT'],
      gasPedal: 5,
      brakePedal: 3,
      escape: 10,
      fastPit: 10,
      trophy: 'gold',
      pitCrewGrade: 'A',
      dnfExempt: true,
    }),
  D('rw25-suarez', 'Daniel Suarez', '99', 'Chevy', 4, 1, ['SKILL', 'STAR', 'SMART'], [], 5, 6, 'bronze', 'C', { dnfExempt: true }),
];

/**
 * THE PRINTED ALTERNATE, AS A DIFFERENCE — D-EZ-76.
 *
 * The sheet prints two sets of absolute numbers, so that is what `ROSTER_2025`
 * records: a transcription's whole job is to say what is on the cardboard. The
 * engine wants the DIFFERENCE, because a difference still means something after
 * a winter has re-rated the man — see the header of `altCards.ts`.
 *
 * ⚠ THE CONVERSION HAPPENS EXACTLY HERE, once, at the moment a card is built.
 * Diffing later, against a re-rated card, would measure the winter as though it
 * were part of the printed difference.
 */
function alternateCardOf(entry: Roster2025Card, base: DriverCard): AlternateCard | null {
  const alt = entry.alternate;
  if (!alt) return null;
  return {
    label: alt.label,
    when: alt.when,
    delta: deltaFrom(base, {
      qualities: [...alt.qualities],
      starred: [...alt.starred],
      ...(alt.gasPedal !== undefined ? { gasPedal: alt.gasPedal } : {}),
      ...(alt.brakePedal !== undefined ? { brakePedal: alt.brakePedal } : {}),
      ...(alt.escape !== undefined ? { escape: alt.escape } : {}),
      ...(alt.fastPit !== undefined ? { fastPit: alt.fastPit } : {}),
      ...(alt.trophy !== undefined ? { trophy: alt.trophy } : {}),
      ...(alt.pitCrewGrade !== undefined ? { pitCrewGrade: alt.pitCrewGrade } : {}),
      ...(alt.dnfExempt !== undefined ? { dnfExempt: alt.dnfExempt } : {}),
    }),
  };
}

/** The card as the engine wants it, before any condition is applied. */
export function baseCard(entry: Roster2025Card): DriverCard {
  const card: DriverCard = {
    driverId: entry.driverId,
    name: entry.name,
    carNumber: entry.carNumber,
    gasPedal: entry.gasPedal,
    brakePedal: entry.brakePedal,
    qualities: [...entry.qualities],
    starred: [...entry.starred],
    escape: entry.escape,
    fastPit: entry.fastPit,
    fastPitDouble: null,
    trophy: entry.trophy,
    pitCrewGrade: entry.pitCrewGrade,
    triangle: entry.triangle,
    // ⚠ The green thumbs-up has to travel ON the card, because the card is the
    // only thing the engine ever sees. `problem.ts` reads it off `car.card`.
    ...(entry.dnfExempt ? { dnfExempt: true } : {}),
    // The nose tab, as printed. See `liveryForDriver`.
    make: entry.manufacturer,
  };
  const alt = alternateCardOf(entry, card);
  return alt ? { ...card, altCard: alt } : card;
}

/**
 * The card this driver actually races off at this racetrack, in his ROOKIE
 * season — which is the only season for which the printed numbers are the
 * answer.
 *
 * Kept because it is how the transcription is checked against the sheet: a test
 * can ask for van Gisbergen's road-course card and compare it, field by field,
 * to the "R" hood. The engine does not call it — it goes through `alternateOf`
 * on whatever the card says today, which for an unraced 2025 card is exactly
 * this and after a winter is not.
 */
export function cardFor(entry: Roster2025Card, at: RaceConditions): DriverCard {
  return alternateOf(baseCard(entry), at);
}

/** Every driver the 2025 set prints a green thumbs-up triangle for. */
export const DNF_EXEMPT_2025: readonly string[] = ROSTER_2025.filter((d) => d.dnfExempt).map((d) => d.driverId);
